import { useEffect, useRef } from 'react';

const STORAGE_KEY = 'cloudamp__data__c';
const POLL_INTERVAL_MS = 1000;
const MAX_POLL_ATTEMPTS = 20;

// localStorage throws in Safari private mode and whenever storage is disabled, which would
// otherwise take a form submission down with it. cloudamp's own ctk.js opts out entirely under
// those conditions, so an empty string is the honest answer.
function readStorage() {
    try {
        return localStorage.getItem(STORAGE_KEY) || '';
    } catch {
        return '';
    }
}

function writeStorage(value) {
    try {
        localStorage.setItem(STORAGE_KEY, value);
    } catch {
        // Storage unavailable. The live hidden input still carries the value into this submission.
    }
}

/**
 * Cloudamp attribution captured on this or an earlier page view, or '' if we never got any.
 *
 * For forms that have no `cloudamp__data__c` input of their own (quote, contact, FAQs, newsletter).
 * They rely on the consult form in the layout, which runs on every page, to have filled
 * localStorage via useCloudampDataRef.
 */
export function readStoredCloudampData() {
    if (typeof window === 'undefined') return '';
    return readStorage();
}

/**
 * The value to submit: the live hidden input if it holds anything, otherwise whatever an earlier
 * page view stored. The fallback matters because React can hand the form a fresh, empty input at
 * any point — see useCloudampDataRef.
 */
export function getCloudampData(cloudampDataRef) {
    return cloudampDataRef?.current?.value || readStoredCloudampData();
}

/**
 * Keeps a hidden `cloudamp__data__c` input populated. Attach the returned ref to that input.
 *
 * ctk.js scans `document.forms` exactly once, when it finishes loading, fills every
 * `cloudamp__data__c` input it finds, and never rescans. Two things routinely lose that value on
 * these sites:
 *
 *   - `Consult` picks between NotOpen/SingleStore/MultipleStores from `siteData.currentMetro`,
 *     which is replaced once the Firebase store fetch resolves. React unmounts the old form and
 *     builds a brand-new hidden input, so anything ctk.js had written is discarded with the old
 *     node. Measured on milanlaseralbuquerque.com, the input is recreated three times inside the
 *     first second of every page load, and only ~1 load in 6 happened to have ctk.js land last.
 *   - ctk.js is fetched async from a CDN, so on a slow connection it can arrive after the form has
 *     settled, or never.
 *
 * So we drive population ourselves instead of trusting ctk.js's single pass: poll, and on each
 * tick ask ctk.js to re-fill whichever node the ref currently points at.
 * `window._cloudAmp.populateForm(form)` reuses the attribution data ctk.js cached on load, so
 * calling it repeatedly is cheap and always yields the same campaign values.
 */
export function useCloudampDataRef() {
    const cloudampDataRef = useRef(null);

    useEffect(() => {
        const capture = () => {
            const input = cloudampDataRef.current;
            if (!input) return;

            // Hand the current node back to ctk.js. Guarded: on the first ticks of a page load
            // ctk.js may not have executed yet, and it stays undefined if the CDN is blocked.
            const form = input.closest('form');
            if (form && typeof window._cloudAmp?.populateForm === 'function') {
                window._cloudAmp.populateForm(form);
            }

            const stored = readStorage();

            // Cross-page fallback, applied only when ctk.js has given us nothing: attribution from
            // an earlier page view beats submitting a blank field.
            if (!input.value && stored) input.value = stored;

            // Persist real values only. The previous implementation wrote the input value
            // unconditionally after 5s, so an empty input erased the fallback that every other
            // form on the site reads.
            if (input.value && input.value !== stored) writeStorage(input.value);
        };

        capture();

        let attempts = 0;
        const interval = setInterval(() => {
            capture();
            if (++attempts >= MAX_POLL_ATTEMPTS) clearInterval(interval);
        }, POLL_INTERVAL_MS);

        return () => clearInterval(interval);
    }, []);

    return cloudampDataRef;
}

export default useCloudampDataRef;
